import {
  Box, Wrap, Text,
  Modal,
  ModalHeader,
  ModalBody,
  ModalContent,
  Flex,
  IconButton,
  Icon,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  FormErrorMessage,
  Grid,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import {
  useState, useEffect, Fragment,
} from 'react';
import {
  rectIntersection,
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FiChevronLeft, FiChevronRight, FiMenu, FiTrash2,
  FiXCircle,
} from 'react-icons/fi';
import { availableReportComponents, reportGroups } from '@src/modules/performance/shared/availableReportComponents';
import { useForm } from 'react-hook-form';
import { useAccessLevels, useCreateCustomReport, useUpdateCustomReport } from '../../context/custom-reports-api-hooks';

const pageOptions = [
  { value: 'Performance', label: 'Performance Dashboard' },
  { value: 'Home', label: 'Home' },
];

const getSchema = (reportNames = [], currentReportName = '') => yup.object({
  name: yup
    .string()
    .transform((value) => value?.trim())
    .required('Report name is required')
    .test(
      'unique-name',
      'A report with this name already exists',
      (value) => {
        if (!value) return true;

        const normalize = (str) => str
          .trim()
          .replace(/\s+/g, ' ')
          .toLowerCase();

        const normalizedValue = normalize(value);
        const currentName = normalize(currentReportName);

        return !reportNames.some(
          (name) => (
            normalize(name) === normalizedValue
            && normalize(name) !== currentName
          ),
        );
      },
    ),
  description: yup
    .string()
    .optional()
    .max(250, 'Description must be 250 characters or less'),
  publishDate: yup
    .string()
    .required('Publish date is required')
    .typeError('Publish date is required'),
  roleAccessLevel: yup
    .number()
    .typeError('Role access level must be a number')
    .required('Access level is required'),
  components: yup
    .array()
    .min(1, 'At least one component must be added'),
});

const getLocalTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function SortableItem(props) {
  const { id, children, onRemove } = props;
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging, isSorting,
  } = useSortable({ id, animateLayoutChanges: () => true });

  const style = {
    position: 'relative',
    transform: isSorting ? undefined : CSS.Translate.toString(transform),
    transition,
    cursor: 'grab',
    backgroundColor: isDragging ? '#f1f1f1' : 'unset',
    borderRadius: 6,
    willChange: 'transform',
    zIndex: isDragging ? 500 : 'inherit',
    flexGrow: id >= 1000 ? 1 : 0, // Only allow flex-grow for report sections
  };

  return (
    <Flex
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      position="relative"
      role="group"
    >
      <Box
        position="absolute"
        top={1}
        right={1}
        zIndex={150}
        opacity={0}
        _groupHover={{ opacity: 1 }}
        transition="opacity 0.2s"
      >
        <IconButton
          size="sm"
          icon={<Icon as={FiTrash2} boxSize={4} />}
          onClick={() => onRemove(id)}
          variant="solid"
          colorScheme="gray"
          display={isDragging ? 'none' : 'block'}
        />
      </Box>
      <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={100} />
      <Flex
        visibility={isDragging ? 'hidden' : 'visible'}
        width="100%"
        zIndex={20}
      >
        {children}
      </Flex>
      <Flex
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex="10"
        align="center"
        justify="center"
        px={8}
        border="1px dotted"
        borderColor="gray.300"
        borderRadius="md"
        display={isDragging ? 'none' : 'flex'}
      >
        <Text fontSize="sm" textAlign="center" color="gray.500">Component unavailable for selected RCA</Text>
      </Flex>
    </Flex>
  );
}

function DraggableItem({ id, isActive, children }) {
  const {
    attributes, listeners, setNodeRef, isDragging,
  } = useDraggable({ id });
  return (
    <Flex
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      cursor={isActive ? 'not-allowed' : 'grab'}
      pointerEvents={isActive ? 'none' : 'auto'}
      willChange="transform"
      width="100%"
      opacity={(isDragging || isActive) ? '0.5' : '1'}
      flexGrow="1"
    >
      <HStack
        backgroundColor="white"
        p={2}
        boxShadow="sm"
        borderRadius="base"
        border="1px solid"
        borderColor="gray.100"
        transition="all 0.2s ease-in-out"
        width="100%"
        _hover={{
          boxShadow: 'md',
          borderColor: 'blue.500',
        }}
      >
        <Icon as={FiMenu} boxSize={4} color="gray.300" />
        {children}
      </HStack>
    </Flex>
  );
}

// DropZone component to show the drop area
function DropZone({ id }) {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <Flex
      ref={setNodeRef}
      width="100%"
      flexGrow="1"
      borderRadius="base"
      backgroundColor={isOver ? 'blue.50' : 'gray.50'}
      padding={4}
      border="2px dashed"
      borderColor={isOver ? 'blue.500' : 'gray.200'}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <VStack>
        <Text textAlign="center">
          <strong>Drag and drop components here.</strong>
        </Text>
        <Text textAlign="center" fontSize="sm" color="gray.500">
          At least one component must be
          <br />
          added to create a report.
        </Text>
      </VStack>
    </Flex>
  );
}

// DropLine component to show the drop line between items
function DropLine() {
  return (
    <Box
      position="relative"
      display="flex"
      flexDirection="column"
      alignItems="center"
      zIndex={900}
      flexGrow="0"
      flexShrink="0"
      flexBasis="0"
      margin="0px!important"
      width="0px"
    >
      <Box
        width="18px"
        height="18px"
        borderRadius="full"
        backgroundColor="white"
        position="absolute"
        top="-8px"
        border="4px solid"
        borderColor="blue.500"
      />
      <Box
        width="6px"
        height="100%"
        backgroundColor="blue.500"
        borderRadius="full"
      />
    </Box>
  );
}

function CustomReportsEdit({
  isOpen, onClose, selectedReport, reportNames,
}) {
  const { data: accessLevels } = useAccessLevels();
  const { mutateAsync: doCreateCustomReport, isLoading: isCreating } = useCreateCustomReport();
  const { mutateAsync: doUpdateCustomReport, isLoading: isUpdating } = useUpdateCustomReport();
  const [activeComponents, setActiveComponents] = useState([]);
  const [filteredComponents, setFilteredComponents] = useState(null);
  const submitButtonLabel = selectedReport ? 'Update Report' : 'Create Report';

  const [selectedGroup, setSelectedGroup] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [dropLinePosition, setDropLinePosition] = useState('start');

  // Pass the reportNames to getSchema to ensure unique names when creating or editing reports
  const schema = getSchema(reportNames, selectedReport?.name || '');

  const {
    register, handleSubmit, formState: { errors }, reset, setValue,
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      publishDate: getLocalTodayDate(),
      roleAccessLevel: accessLevels?.[0]?.id || 10, // Default to first access level
      page: [...pageOptions.map((option) => option.value)], // Default to all page options
    },
    resolver: yupResolver(schema),
  });

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (over) {
      setOverId(over.id);

      // Check if the dragged item (active) is in activeComponents or availableReportComponents
      const isDraggingActiveComponent = activeComponents.some(({ id }) => id === active.id);
      const isOverActiveComponent = activeComponents.some(({ id }) => id === over.id);

      if (isDraggingActiveComponent && isOverActiveComponent) {
      // Reordering within activeComponents
        const activeIndex = activeComponents.findIndex(({ id }) => id === active.id);
        const overIndex = activeComponents.findIndex(({ id }) => id === over.id);

        if (activeIndex > overIndex) setDropLinePosition('start');
        else setDropLinePosition('end');
      } else if (!isDraggingActiveComponent && isOverActiveComponent) {
        setDropLinePosition('start');
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active && over) {
    // Determine if the item being dragged is from availableReportComponents or activeComponents
      const isDraggingActiveComponent = activeComponents.some(({ id }) => id === active.id);
      const draggedComponent = isDraggingActiveComponent
        ? activeComponents.find((comp) => comp.id === active.id)
        : availableReportComponents.find((comp) => comp.id === active.id);

      if (!draggedComponent) return;

      if (!isDraggingActiveComponent) {
      // New component dragged into activeComponents
        const overIndex = activeComponents.findIndex(({ id }) => id === over.id);

        if (overIndex === -1) {
        // If the overId isn't found in activeComponents, append at the end
          setActiveComponents((prev) => [...prev, draggedComponent]);
        } else {
        // Insert the new component at the position it's being dropped over
          const updatedComponents = [...activeComponents];
          updatedComponents.splice(overIndex, 0, draggedComponent); // Insert at the overIndex
          setActiveComponents(updatedComponents);
        }
      } else {
      // Reordering within activeComponents
        const oldIndex = activeComponents.findIndex(({ id }) => id === active.id);
        const newIndex = activeComponents.findIndex(({ id }) => id === over.id);

        if (oldIndex !== newIndex) {
          const updatedComponents = [...activeComponents];
          updatedComponents.splice(oldIndex, 1);
          updatedComponents.splice(newIndex, 0, activeComponents[oldIndex]);
          setActiveComponents(updatedComponents);
        }
      }
    }

    setActiveId(null);
    setOverId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
  };

  const handleRemoveItem = (id) => {
    setActiveComponents((prev) => prev.filter((component) => component.id !== id));
  };

  const resetForm = () => {
    reset({
      name: '',
      description: '',
      publishDate: getLocalTodayDate(),
      roleAccessLevel: accessLevels?.[0]?.id || 10,
      page: [...pageOptions.map((option) => option.value)],
      components: [],
    });
    setActiveComponents([]);
    setSelectedGroup(null);
    setActiveId(null);
    setOverId(null);
    setDropLinePosition('start');
  };

  useEffect(() => {
    if (selectedReport) {
      reset({
        name: selectedReport?.name || '',
        description: selectedReport?.description || '',
        publishDate: selectedReport?.publishDate || getLocalTodayDate(),
        roleAccessLevel: selectedReport?.roleAccessLevel || accessLevels?.[0]?.id,
        page: [...pageOptions.map((option) => option.value)],
        components: selectedReport?.components || [],
      });
      setActiveComponents(
        selectedReport?.components?.map((id) => {
          // Find the component based on the id and return it
          const component = availableReportComponents.find(({ id: compId }) => compId === id);
          if (component) {
            return (
              component
            );
          }
          return null;
        }),
      );
    } else {
      resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  // Update the form value for components whenever activeComponents changes
  useEffect(() => {
    setValue('components', activeComponents.map(({ id }) => id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeComponents]);

  const onSubmit = async (data) => {
    const formData = {
      ...selectedReport,
      components: activeComponents.map(({ id }) => id),
      name: data.name,
      description: data.description,
      publishDate: data.publishDate || null,
      roleAccessLevel: parseInt(data.roleAccessLevel, 10),
      page: data.page,
    };

    if (formData.id) {
      await doUpdateCustomReport(formData);
    } else {
      await doCreateCustomReport(formData);
    }
    onClose();
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const [searchQuery, setSearchQuery] = useState('');
  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setSearchQuery(searchTerm);
  };

  const handleClearSearch = () => {
    setFilteredComponents(null);
    setSearchQuery('');
  };

  useEffect(() => {
    if (searchQuery) {
      const filtered = availableReportComponents.filter((component) => component.title.toLowerCase().startsWith(searchQuery));
      setFilteredComponents(filtered);
    } else {
      setFilteredComponents(null);
    }
  }, [searchQuery]);
  /* eslint-disable no-unused-vars */
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="full"
      scrollBehavior="inside"
    >
      <ModalContent
        borderRadius="none"
      >
        <ModalHeader p={4} display="flex" justifyContent="space-between" alignItems="center">
          Custom Report Editor
          <HStack>
            <Button
              variant="outline"
              colorScheme="gray"
              size="md"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              size="md"
              onClick={handleSubmit(onSubmit)}
              isLoading={isCreating || isUpdating}
            >
              {submitButtonLabel}
            </Button>
          </HStack>
        </ModalHeader>
        <ModalBody
          background="gray.50"
          p={0}
          borderTop="1px solid"
          borderColor="gray.100"
          display="flex"
          flexDirection="column"
          height="100%"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragOver={handleDragOver}
          >
            <Flex
              flexDir="row"
              width="100%"
              height="100%"
              flexGrow="1"
            >
              <Flex
                position="sticky"
                p={0}
                background="white"
                boxShadow="md"
                flexDirection="column"
                minWidth="350px"
              >
                <Flex
                  direction="column"
                  position="sticky"
                  top="0"
                  flexGrow="1"
                >
                  <VStack p={4} spacing={2} align="stretch">
                    <FormControl isInvalid={!!errors.name}>
                      <Input
                        placeholder="Enter report name"
                        size="sm"
                        {...register('name')}
                      />
                      <FormErrorMessage>{errors.name && errors.name.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors.description}>
                      <Textarea
                        placeholder="Enter report description"
                        size="sm"
                        rows={4}
                        {...register('description')}
                      />
                      <FormErrorMessage>{errors.description && errors.description.message}</FormErrorMessage>
                    </FormControl>
                    <HStack spacing={2} align="flex-start">
                      <FormControl isInvalid={!!errors.publishDate}>
                        <FormLabel fontSize="xs">Publish Date</FormLabel>
                        <Input
                          type="date"
                          size="sm"
                          {...register('publishDate')}
                        />
                        <FormErrorMessage>{errors.publishDate && errors.publishDate.message}</FormErrorMessage>
                      </FormControl>
                      <FormControl isInvalid={!!errors.roleAccessLevel}>
                        <FormLabel fontSize="xs">Access Level</FormLabel>
                        <Select
                          size="sm"
                          {...register('roleAccessLevel')}
                        >
                          {accessLevels?.map(({ id, name }) => (
                            <option key={id} value={id}>
                              {name}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{errors.roleAccessLevel && errors.roleAccessLevel.message}</FormErrorMessage>
                      </FormControl>
                    </HStack>
                  </VStack>
                  <Flex
                    flexGrow="1"
                    flexDirection="column"
                  >
                    <Box
                      px={4}
                      py={2}
                      borderTop="1px solid"
                      borderColor="gray.100"
                    >
                      <Text fontSize="sm" fontWeight="bold">Report Components</Text>
                      {errors.components && (
                        <Text color="red.500" fontSize="sm">{errors.components.message}</Text>
                      )}
                    </Box>
                    {!selectedGroup && (
                      <Box
                        px={4}
                        pb={2}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        <InputGroup size="sm">
                          <Input
                            placeholder="Search components..."
                            size="sm"
                            value={searchQuery}
                            onChange={handleSearch}
                          />
                          <InputRightElement>
                            {searchQuery && (
                            <IconButton
                              size="xs"
                              variant="ghost"
                              colorScheme="gray"
                              icon={<Icon as={FiXCircle} boxSize={4} />}
                              aria-label="Clear search"
                              onClick={handleClearSearch}
                            />
                            )}
                          </InputRightElement>
                        </InputGroup>
                      </Box>
                    )}
                    {!selectedGroup && searchQuery && (
                      <>
                        <Text fontSize="sm" px={4} pt={2}>Drag and drop items onto the report page</Text>
                        {filteredComponents && filteredComponents.length > 0 ? (
                          <VStack spacing={1} p={4}>
                            {filteredComponents
                            // Sort by title
                            // ensure type === dashboard-widget is first
                              .sort((a, b) => {
                                if (a.type === 'dashboard-widget' && b.type !== 'dashboard-widget') return -1;
                                if (b.type === 'dashboard-widget' && a.type !== 'dashboard-widget') return 1;
                                return a.title.localeCompare(b.title);
                              })
                              .map(({ id, title }) => (
                                <DraggableItem
                                  key={`draggable${id}`}
                                  id={activeComponents.some((comp) => comp.id === id) ? null : id}
                                  isActive={activeComponents.some((comp) => comp.id === id)}
                                >
                                  <Text fontSize="xs">{title}</Text>
                                </DraggableItem>
                              ))}
                          </VStack>
                        ) : (
                          <Text fontSize="sm" px={4} py={2} color="gray.500">
                            No components found
                          </Text>
                        )}
                      </>
                    )}
                    {!selectedGroup && !filteredComponents && (
                      <VStack p={4} spacing={1}>
                        {reportGroups
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((group) => (
                            <HStack
                              key={group.id}
                              cursor="pointer"
                              backgroundColor={selectedGroup === group ? 'blue.50' : 'white'}
                              borderRadius="base"
                              pl={3}
                              pr={2}
                              py={1}
                              onClick={() => setSelectedGroup(group)}
                              width="100%"
                              border="1px solid"
                              borderColor="gray.100"
                              transition="all 0.2s ease-in-out"
                              justify="space-between"
                              _hover={{
                                borderColor: 'blue.500',
                              }}
                            >
                              <Text fontSize="xs" color={selectedGroup === group ? 'blue.500' : 'gray.700'}>
                                {group.name}
                              </Text>
                              <Icon as={FiChevronRight} boxSize={4} color={selectedGroup === group ? 'blue.500' : 'gray.300'} />
                            </HStack>
                          ))}
                      </VStack>
                    )}
                    {selectedGroup && !searchQuery && (
                    <>
                      <HStack
                        onClick={() => setSelectedGroup(null)}
                        px={4}
                        py={2}
                        mb={2}
                        cursor="pointer"
                        borderBottom="1px solid"
                        borderTop="1px solid"
                        borderColor="gray.100"
                        transition="all 0.2s ease-in-out"
                        _hover={{
                          color: 'blue.500',
                        }}
                      >
                        <Icon as={FiChevronLeft} boxSize={5} onClick={() => setSelectedGroup(null)} />
                        <Text fontWeight="bold" fontSize="sm" color="inherit">{selectedGroup.name}</Text>
                      </HStack>
                      <Text fontSize="sm" px={4}>Drag and drop items onto the report page</Text>
                      <VStack spacing={1} p={4}>
                        {availableReportComponents
                        // Sort by title
                        // ensure type === dashboard-widget is first
                          .sort((a, b) => {
                            if (a.type === 'dashboard-widget' && b.type !== 'dashboard-widget') return -1;
                            if (b.type === 'dashboard-widget' && a.type !== 'dashboard-widget') return 1;
                            return a.title.localeCompare(b.title);
                          })
                          .filter(({ groupId }) => groupId === selectedGroup.id)
                          .map(({ id, title }) => (
                            <DraggableItem
                              key={`draggable${id}`}
                              id={activeComponents.some((comp) => comp.id === id) ? null : id}
                              isActive={activeComponents.some((comp) => comp.id === id)}
                            >
                              <Text fontSize="xs">{title}</Text>
                            </DraggableItem>
                          ))}
                      </VStack>
                    </>
                    )}
                  </Flex>
                </Flex>
              </Flex>
              <Flex
                p={4}
                flexGrow="1"
              >
                <SortableContext
                  items={[...availableReportComponents.map(({ id }) => id)]}
                  strategy={rectSortingStrategy}
                >
                  {activeComponents.length > 0 && (
                    activeComponents?.every(({ type }) => type === 'dashboard-widget') ? (
                      <Grid
                        className="dashboard-grid"
                        gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
                        gap={2}
                        width="100%"
                      >
                        {activeComponents.map(({ component: Component, id }) => (
                          <Fragment key={id}>
                            {overId === id && activeId !== id && dropLinePosition === 'start' && (
                              <DropLine />
                            )}
                            <SortableItem key={`sortable${id}`} id={id} onRemove={handleRemoveItem}>
                              {Component}
                            </SortableItem>
                            {overId === id && activeId !== id && dropLinePosition === 'end' && (
                              <DropLine />
                            )}
                          </Fragment>
                        ))}
                      </Grid>
                    ) : (
                      <Wrap spacing={2} width="100%" flexGrow="1">
                        {activeComponents.map(({ component: Component, id }) => (
                          <Fragment key={id}>
                            {overId === id && activeId !== id && dropLinePosition === 'start' && (
                              <DropLine />
                            )}
                            <SortableItem key={`sortable${id}`} id={id} onRemove={handleRemoveItem}>
                              {Component}
                            </SortableItem>
                            {overId === id && activeId !== id && dropLinePosition === 'end' && (
                              <DropLine />
                            )}
                          </Fragment>
                        ))}
                      </Wrap>
                    )
                  )}
                  {activeComponents.length === 0 && (
                    <DropZone id="empty-dropzone" />
                  )}
                </SortableContext>
                <DragOverlay>
                  {activeId ? (
                    <Flex position="relative">
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        zIndex={100}
                        cursor="grabbing"
                      />
                      {activeComponents.find(({ id }) => id === activeId)
                        ? activeComponents.find(({ id }) => id === activeId).component
                        : (
                          availableReportComponents.find(({ id }) => id === activeId)?.component
                        )}
                    </Flex>
                  ) : null}
                </DragOverlay>
              </Flex>
            </Flex>
          </DndContext>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default CustomReportsEdit;
